import type { ContractorTimeEntry } from '../types';

/** Maximum billable hours a contractor may log on one calendar day. */
export const CONTRACTOR_MAX_DAILY_HOURS = 16;
export const CONTRACTOR_MAX_DAILY_MINUTES = CONTRACTOR_MAX_DAILY_HOURS * 60;

export function contractorWorkDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function sumDraftMinutesForDate(
  entries: Pick<
    ContractorTimeEntry,
    'workDate' | 'minutes' | 'status' | 'id' | 'dailyCapGrandfathered'
  >[],
  dateKey: string,
  excludeEntryId?: string,
): number {
  return entries.reduce((sum, entry) => {
    if (entry.status !== 'draft' || entry.minutes <= 0) return sum;
    if (entry.dailyCapGrandfathered) return sum;
    if (excludeEntryId && entry.id === excludeEntryId) return sum;
    if (contractorWorkDateKey(entry.workDate) !== dateKey) return sum;
    return sum + entry.minutes;
  }, 0);
}

export function remainingDailyMinutes(
  entries: Pick<
    ContractorTimeEntry,
    'workDate' | 'minutes' | 'status' | 'id' | 'dailyCapGrandfathered'
  >[],
  dateKey: string,
  excludeEntryId?: string,
): number {
  const used = sumDraftMinutesForDate(entries, dateKey, excludeEntryId);
  return Math.max(0, CONTRACTOR_MAX_DAILY_MINUTES - used);
}

export function wouldExceedContractorDailyCap(
  entries: Pick<
    ContractorTimeEntry,
    'workDate' | 'minutes' | 'status' | 'id' | 'dailyCapGrandfathered'
  >[],
  workDate: Date,
  additionalMinutes: number,
  excludeEntryId?: string,
): boolean {
  if (additionalMinutes <= 0) return false;
  const dateKey = contractorWorkDateKey(workDate);
  const used = sumDraftMinutesForDate(entries, dateKey, excludeEntryId);
  return used + additionalMinutes > CONTRACTOR_MAX_DAILY_MINUTES;
}

export function formatContractorDailyCapError(args: {
  workDate: Date;
  existingMinutes: number;
  additionalMinutes: number;
}): string {
  const { workDate, existingMinutes, additionalMinutes } = args;
  const dateLabel = workDate.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  const existingHours = (existingMinutes / 60).toFixed(2);
  const attemptedTotal = ((existingMinutes + additionalMinutes) / 60).toFixed(2);
  const remaining = Math.max(0, CONTRACTOR_MAX_DAILY_MINUTES - existingMinutes);
  const remainingHours = (remaining / 60).toFixed(2);

  if (existingMinutes > 0) {
    return `${dateLabel} already has ${existingHours}h logged. Adding ${(additionalMinutes / 60).toFixed(2)}h would total ${attemptedTotal}h — each day is capped at ${CONTRACTOR_MAX_DAILY_HOURS} hours. You can add up to ${remainingHours}h more on ${dateLabel}; log other days separately.`;
  }

  return `Each day is capped at ${CONTRACTOR_MAX_DAILY_HOURS} hours. ${(additionalMinutes / 60).toFixed(2)}h exceeds that limit for ${dateLabel}.`;
}
